import { type PivotConfig, configPorDefecto, usePivotFavoritas } from '@/hooks/usePivotFavoritas';
import { exportarPivotExcel } from '@/lib/exportarPivotExcel';
import { CaretDown, CaretRight, DownloadSimple, Star, X } from '@phosphor-icons/react';
import { type DragEvent, type ReactElement, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Tabla dinámica (pivote) a medida, inspirada en el prototipo "Tabla Dinámica":
 * - Chips de campos arrastrables a zonas FILAS / COLUMNAS / MÉTRICAS.
 * - Toggle de Agregación (Suma / Promedio / Conteo / Máximo).
 * - Columnas agrupadas por los valores de las dimensiones de columna + Total.
 * - Filas anidadas COLAPSABLES con subtotales por nivel.
 * - Heatmap azul en celdas hoja; formato ₡ con miles separados por espacio.
 *
 * Agnóstica de datos: recibe `datos` (array de registros) y `campos` (catálogo).
 * Los subtotales/totales se calculan desde los registros (acumulador
 * combinable sum/count/max), así que Suma, Promedio, Conteo y Máximo son
 * correctos a cualquier nivel.
 */

export type TipoCampo = 'dimension' | 'metrica';
export interface Campo {
  id: string;
  etiqueta: string;
  tipo: TipoCampo;
  /** Formato de la métrica (default 'numero'). */
  formato?: 'moneda' | 'numero';
}
export type Registro = Record<string, string | number | null>;

type Agregacion = 'Suma' | 'Promedio' | 'Conteo' | 'Máximo';
const AGREGACIONES: Agregacion[] = ['Suma', 'Promedio', 'Conteo', 'Máximo'];

type Zona = 'filas' | 'columnas' | 'metricas' | 'filtros' | 'disponibles';

const SEP = '';
const TOTAL = 'TOTAL';

interface Acc {
  sum: number;
  count: number;
  max: number;
}
const nuevoAcc = (): Acc => ({ sum: 0, count: 0, max: Number.NEGATIVE_INFINITY });
const pushAcc = (a: Acc, v: number) => {
  a.sum += v;
  a.count += 1;
  if (v > a.max) a.max = v;
};
const valorAcc = (a: Acc | undefined, agg: Agregacion): number => {
  if (!a || a.count === 0) return Number.NaN;
  switch (agg) {
    case 'Suma':
      return a.sum;
    case 'Promedio':
      return a.sum / a.count;
    case 'Conteo':
      return a.count;
    case 'Máximo':
      return a.max;
  }
};

interface NodoFila {
  path: string[];
  etiqueta: string;
  hijos: Map<string, NodoFila>;
  /** accs por clave `${colStr}${SEP}${metricaId}`. */
  accs: Map<string, Acc>;
  esHoja: boolean;
}

function formatearValor(v: number, formato: 'moneda' | 'numero', agg: Agregacion): string {
  if (Number.isNaN(v)) return '—';
  const dec = agg === 'Conteo' ? 0 : formato === 'moneda' ? 0 : 1;
  const neg = v < 0;
  const partes = Math.abs(v).toFixed(dec).split('.');
  const ent = partes[0] ?? '0';
  const frac = partes[1];
  const entConEspacios = ent.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const cuerpo = (neg ? '-' : '') + entConEspacios + (frac ? `,${frac}` : '');
  return agg !== 'Conteo' && formato === 'moneda' ? `₡${cuerpo}` : cuerpo;
}

/** Color de fondo heatmap azul según intensidad 0..1. */
function colorHeatmap(intensidad: number): string {
  if (Number.isNaN(intensidad)) return '';
  const alpha = 0.08 + intensidad * 0.45;
  return `rgba(59, 130, 246, ${alpha.toFixed(3)})`;
}

export function TablaPivote({
  datos,
  campos,
  filasIniciales = [],
  columnasIniciales = [],
  metricasIniciales = [],
  tituloExport = 'Tabla Dinámica',
  nombreArchivoExport = 'tabla-dinamica',
  favoritasKey,
}: {
  datos: Registro[];
  campos: Campo[];
  filasIniciales?: string[];
  columnasIniciales?: string[];
  metricasIniciales?: string[];
  /** Título de la hoja del Excel exportado. */
  tituloExport?: string;
  /** Nombre base del archivo .xlsx (sin extensión). */
  nombreArchivoExport?: string;
  /** Si se pasa, habilita las vistas favoritas (persistidas por reporte). */
  favoritasKey?: string;
}): ReactElement {
  // Vista inicial: la favorita marcada por defecto (si existe) o los props.
  const favInicial = useMemo(
    () => (favoritasKey ? configPorDefecto(favoritasKey) : null),
    [favoritasKey],
  );
  const [filas, setFilas] = useState<string[]>(() => favInicial?.filas ?? filasIniciales);
  const [columnas, setColumnas] = useState<string[]>(
    () => favInicial?.columnas ?? columnasIniciales,
  );
  const [metricas, setMetricas] = useState<string[]>(
    () => favInicial?.metricas ?? metricasIniciales,
  );
  const [filtrosDim, setFiltrosDim] = useState<string[]>(() => favInicial?.filtrosDim ?? []);
  // Valores seleccionados por dimensión-filtro. Si una dim no está en el mapa o
  // su set está "todos", no filtra. Guardamos el set de valores INCLUIDOS.
  const [valoresFiltro, setValoresFiltro] = useState<Record<string, Set<string>>>({});
  const [agregacion, setAgregacion] = useState<Agregacion>(
    () => (favInicial?.agregacion as Agregacion) ?? 'Suma',
  );
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [anchoCol, setAnchoCol] = useState(360);
  // Orden de filas por una columna+métrica. null = orden natural (alfabético).
  // dir: 'desc' (mayor→menor) | 'asc' (menor→mayor).
  const [orden, setOrden] = useState<{ ck: string; m: string; dir: 'asc' | 'desc' } | null>(null);

  const porId = useMemo(() => new Map(campos.map((c) => [c.id, c])), [campos]);
  const disponibles = useMemo(() => {
    const enZonas = new Set([...filas, ...columnas, ...metricas, ...filtrosDim]);
    return campos.filter((c) => !enZonas.has(c.id));
  }, [campos, filas, columnas, metricas, filtrosDim]);

  // --- Vistas favoritas (persistidas por reporte) ---
  const favs = usePivotFavoritas(favoritasKey ?? '');
  const soloExistentes = (ids: string[]) => ids.filter((id) => porId.has(id));
  const aplicarConfig = (c: PivotConfig) => {
    setFilas(soloExistentes(c.filas));
    setColumnas(soloExistentes(c.columnas));
    setMetricas(soloExistentes(c.metricas));
    setFiltrosDim(soloExistentes(c.filtrosDim));
    setValoresFiltro({});
    setAgregacion((c.agregacion as Agregacion) ?? 'Suma');
  };
  const guardarFav = () => {
    const nombre = window.prompt('Nombre de la vista favorita:');
    if (nombre) favs.guardar(nombre, { filas, columnas, metricas, filtrosDim, agregacion });
  };
  const restablecer = () =>
    aplicarConfig({
      filas: filasIniciales,
      columnas: columnasIniciales,
      metricas: metricasIniciales,
      filtrosDim: [],
      agregacion: 'Suma',
    });

  // Quita una dimensión SOLO de Filtros (deja intacta su colocación en
  // filas/columnas/métricas). Usado por la X del chip de filtro.
  const quitarFiltro = (id: string) => {
    setFiltrosDim((p) => p.filter((x) => x !== id));
    setValoresFiltro((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const mover = (id: string, destino: Zona) => {
    const campo = porId.get(id);
    if (!campo) return;
    // Validar tipo según destino. Filtros y filas/columnas: solo dimensiones.
    if (
      (destino === 'filas' || destino === 'columnas' || destino === 'filtros') &&
      campo.tipo !== 'dimension'
    )
      return;
    if (destino === 'metricas' && campo.tipo !== 'metrica') return;

    // Filtros es una zona INDEPENDIENTE (como en Excel): una dimensión puede
    // estar en Filtros a la vez que en Filas o Columnas. Por eso al soltar en
    // Filtros solo la agregamos, sin quitarla de su zona de colocación.
    if (destino === 'filtros') {
      setFiltrosDim((p) => (p.includes(id) ? p : [...p, id]));
      return;
    }

    // Zonas de COLOCACIÓN (filas/columnas/métricas) mutuamente excluyentes.
    // Soltar en "disponibles" solo la quita de la colocación; su filtro (si lo
    // tiene) se mantiene y se administra con la X del chip de filtro.
    setFilas((p) => p.filter((x) => x !== id));
    setColumnas((p) => p.filter((x) => x !== id));
    setMetricas((p) => p.filter((x) => x !== id));
    if (destino === 'filas') setFilas((p) => [...p, id]);
    else if (destino === 'columnas') setColumnas((p) => [...p, id]);
    else if (destino === 'metricas') setMetricas((p) => [...p, id]);
  };

  // Valores únicos por dimensión-filtro (para los popups de selección).
  const valoresUnicos = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const dim of filtrosDim) {
      const set = new Set<string>();
      for (const rec of datos) set.add(String(rec[dim] ?? '(—)'));
      m.set(
        dim,
        [...set].sort((a, b) => a.localeCompare(b, 'es', { numeric: true })),
      );
    }
    return m;
  }, [datos, filtrosDim]);

  // Dataset filtrado: aplica los filtros activos antes de pivotar.
  const datosFiltrados = useMemo(() => {
    const activos = filtrosDim.filter((d) => {
      const sel = valoresFiltro[d];
      // Filtra solo si hay selección y NO incluye todos los valores.
      return sel && sel.size > 0 && sel.size < (valoresUnicos.get(d)?.length ?? 0);
    });
    if (activos.length === 0) return datos;
    return datos.filter((rec) =>
      activos.every((d) => valoresFiltro[d]?.has(String(rec[d] ?? '(—)'))),
    );
  }, [datos, filtrosDim, valoresFiltro, valoresUnicos]);

  // ----- Cómputo del pivote -----
  const { raiz, colKeys, maxIntensidad, hayMetricas } = useMemo(() => {
    const raiz: NodoFila = {
      path: [],
      etiqueta: '',
      hijos: new Map(),
      accs: new Map(),
      esHoja: false,
    };
    const colSet = new Set<string>();
    const metricasActivas = metricas.length > 0 ? metricas : [];

    const acumularNodo = (nodo: NodoFila, colStr: string, rec: Registro) => {
      for (const m of metricasActivas) {
        const v = Number(rec[m] ?? 0);
        for (const ck of [colStr, TOTAL]) {
          const clave = `${ck}${SEP}${m}`;
          let acc = nodo.accs.get(clave);
          if (!acc) {
            acc = nuevoAcc();
            nodo.accs.set(clave, acc);
          }
          pushAcc(acc, Number.isFinite(v) ? v : 0);
        }
      }
    };

    for (const rec of datosFiltrados) {
      const colStr =
        columnas.length > 0 ? columnas.map((c) => String(rec[c] ?? '(—)')).join(SEP) : '';
      if (columnas.length > 0) colSet.add(colStr);
      const rowVals = filas.map((f) => String(rec[f] ?? '(—)'));
      let nodo = raiz;
      acumularNodo(nodo, colStr, rec);
      for (let i = 0; i < rowVals.length; i++) {
        const seg = rowVals[i] ?? '(—)';
        let hijo = nodo.hijos.get(seg);
        if (!hijo) {
          hijo = {
            path: rowVals.slice(0, i + 1),
            etiqueta: seg,
            hijos: new Map(),
            accs: new Map(),
            esHoja: false,
          };
          nodo.hijos.set(seg, hijo);
        }
        acumularNodo(hijo, colStr, rec);
        if (i === rowVals.length - 1) hijo.esHoja = true;
        nodo = hijo;
      }
    }

    const colKeys = [...colSet].sort((a, b) => a.localeCompare(b, 'es', { numeric: true }));

    // Heatmap: máximo absoluto por (colStr, metrica) sobre nodos hoja.
    const maxIntensidad = new Map<string, number>();
    const recorrerHojas = (nodo: NodoFila) => {
      for (const hijo of nodo.hijos.values()) {
        if (hijo.hijos.size === 0) {
          for (const [clave, acc] of hijo.accs) {
            const v = Math.abs(valorAcc(acc, agregacion));
            if (!Number.isNaN(v))
              maxIntensidad.set(clave, Math.max(maxIntensidad.get(clave) ?? 0, v));
          }
        } else recorrerHojas(hijo);
      }
    };
    recorrerHojas(raiz);

    return { raiz, colKeys, maxIntensidad, hayMetricas: metricasActivas.length > 0 };
  }, [datosFiltrados, filas, columnas, metricas, agregacion]);

  const toggle = (key: string) =>
    setExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Por defecto, abrir el nivel 0 (categorías) cuando cambia la configuración de
  // filas, para que el usuario vea el primer nivel desplegado (pero pueda
  // colapsarlo). Solo agrega las keys de nivel 0 que aún no estén en el set.
  // biome-ignore lint/correctness/useExhaustiveDependencies: raiz se recalcula con filas/datos
  useEffect(() => {
    const nivel0 = [...raiz.hijos.values()].map((h) => h.path.join(SEP));
    setExpandidos((prev) => {
      const next = new Set(prev);
      for (const k of nivel0) next.add(k);
      return next;
    });
  }, [filas]);

  const ramaKeys = useMemo(() => {
    const keys = new Set<string>();
    const visitar = (nodo: NodoFila) => {
      for (const hijo of nodo.hijos.values()) {
        if (hijo.hijos.size > 0) {
          keys.add(hijo.path.join(SEP));
          visitar(hijo);
        }
      }
    };
    visitar(raiz);
    return keys;
  }, [raiz]);

  // Columnas a renderizar: grupos por colKey + Total. Cada grupo × cada métrica.
  const gruposCol = columnas.length > 0 ? [...colKeys, TOTAL] : [TOTAL];
  const etiquetaGrupo = (ck: string) => (ck === TOTAL ? 'Total' : ck.split(SEP).join(' / '));

  // Celda de valor para un nodo en (grupoCol, metrica).
  const celda = (nodo: NodoFila, ck: string, m: string) => {
    const acc = nodo.accs.get(`${ck}${SEP}${m}`);
    return valorAcc(acc, agregacion);
  };

  // Resize de la columna de jerarquía (arrastrar el handle del header).
  const estiloCol = { width: anchoCol, minWidth: anchoCol, maxWidth: anchoCol };
  const iniciarResize = (startX: number) => {
    const startW = anchoCol;
    const onMove = (ev: globalThis.MouseEvent) =>
      setAnchoCol(Math.max(160, Math.min(900, startW + ev.clientX - startX)));
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Aplanar el árbol respetando expansión. Todos los niveles (incluido el 0)
  // son colapsables: el nivel 0 arranca abierto por defecto (ver expandidosInit)
  // pero se puede cerrar.
  // Tinte de fondo de subtotal según profundidad: más oscuro arriba.
  const bgSubtotal = (depth: number): string => {
    if (depth === 0) return 'bg-emerald-100';
    if (depth === 1) return 'bg-emerald-50';
    return 'bg-lime-50/60';
  };
  // Hijos de un nodo, ordenados por la columna+métrica activa (si hay orden),
  // o en orden natural (inserción/alfabético). El orden se aplica a TODOS los
  // niveles de la jerarquía de forma consistente.
  const hijosOrdenados = (nodo: NodoFila): NodoFila[] => {
    const arr = [...nodo.hijos.values()];
    if (!orden) return arr;
    const factor = orden.dir === 'desc' ? -1 : 1;
    return arr.sort((a, b) => {
      const va = celda(a, orden.ck, orden.m);
      const vb = celda(b, orden.ck, orden.m);
      const na = Number.isNaN(va) ? Number.NEGATIVE_INFINITY : va;
      const nb = Number.isNaN(vb) ? Number.NEGATIVE_INFINITY : vb;
      return (na - nb) * factor;
    });
  };

  // Exportar a Excel el árbol COMPLETO (todos los niveles, sin importar el
  // estado de colapso de la UI) + subtotales por nivel + fila de Gran Total.
  const exportar = () => {
    const columnasExport = gruposCol.flatMap((ck) =>
      metricas.map((m) => ({
        etiquetaGrupo: etiquetaGrupo(ck),
        etiquetaMetrica: porId.get(m)?.etiqueta ?? m,
        formato: (porId.get(m)?.formato ?? 'numero') as 'moneda' | 'numero',
      })),
    );

    const celdasDeNodo = (nodo: NodoFila) =>
      gruposCol.flatMap((ck) =>
        metricas.map((m) => {
          const v = celda(nodo, ck, m);
          const esTotal = ck === TOTAL;
          const esSub = nodo.hijos.size > 0;
          const heat =
            esSub || esTotal || Number.isNaN(v)
              ? null
              : Math.abs(v) / (maxIntensidad.get(`${ck}${SEP}${m}`) || 1);
          return { valor: v, heat };
        }),
      );

    const filasExport: {
      depth: number;
      etiqueta: string;
      esSubtotal: boolean;
      esGranTotal?: boolean;
      celdas: { valor: number; heat: number | null }[];
    }[] = [];
    const recorrer = (nodo: NodoFila) => {
      for (const hijo of hijosOrdenados(nodo)) {
        filasExport.push({
          depth: hijo.path.length - 1,
          etiqueta: hijo.etiqueta,
          esSubtotal: hijo.hijos.size > 0,
          celdas: celdasDeNodo(hijo),
        });
        if (hijo.hijos.size > 0) recorrer(hijo);
      }
    };
    recorrer(raiz);
    // Gran Total al final, a partir del nodo raíz.
    filasExport.push({
      depth: 0,
      etiqueta: 'Gran Total',
      esSubtotal: false,
      esGranTotal: true,
      celdas: celdasDeNodo(raiz),
    });

    exportarPivotExcel({
      nombreArchivo: nombreArchivoExport,
      tituloHoja: tituloExport,
      encabezadoJerarquia: filas.map((f) => porId.get(f)?.etiqueta ?? f).join(' › '),
      columnas: columnasExport,
      filas: filasExport,
      agregacion,
    });
  };

  const filasRender: ReactElement[] = [];
  const renderNivel = (nodo: NodoFila) => {
    for (const hijo of hijosOrdenados(nodo)) {
      const key = hijo.path.join(SEP);
      const tieneHijos = hijo.hijos.size > 0;
      const depth = hijo.path.length - 1;
      const abierto = expandidos.has(key);
      const esSubtotal = tieneHijos;
      const tinte = esSubtotal ? bgSubtotal(depth) : 'bg-white';
      filasRender.push(
        <tr
          key={key}
          className={esSubtotal ? `${bgSubtotal(depth)} font-semibold` : 'hover:bg-zinc-50'}
        >
          <td
            className={`sticky left-0 z-10 border-b border-r border-zinc-200 px-2 py-1 text-xs ${tinte}`}
            style={estiloCol}
          >
            <div className="flex items-center gap-1" style={{ paddingLeft: `${depth * 18}px` }}>
              {tieneHijos ? (
                <button
                  type="button"
                  onClick={() => toggle(key)}
                  className="rounded p-0.5 hover:bg-emerald-200"
                  title={abierto ? 'Colapsar' : 'Expandir'}
                >
                  {abierto ? (
                    <CaretDown className="h-3 w-3" weight="bold" />
                  ) : (
                    <CaretRight className="h-3 w-3" weight="bold" />
                  )}
                </button>
              ) : (
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" />
              )}
              <span className="truncate">{hijo.etiqueta}</span>
            </div>
          </td>
          {gruposCol.map((ck) =>
            metricas.map((m) => {
              const campo = porId.get(m);
              const v = celda(hijo, ck, m);
              const esTotal = ck === TOTAL;
              const intensidad =
                esSubtotal || esTotal || Number.isNaN(v)
                  ? Number.NaN
                  : Math.abs(v) / (maxIntensidad.get(`${ck}${SEP}${m}`) || 1);
              const bg = esTotal ? 'rgba(16,185,129,0.08)' : colorHeatmap(intensidad);
              return (
                <td
                  key={`${ck}${SEP}${m}`}
                  className="whitespace-nowrap border-b border-l border-zinc-100 px-2 py-1 text-right text-xs tabular-nums"
                  style={bg ? { backgroundColor: bg } : undefined}
                >
                  {formatearValor(v, campo?.formato ?? 'numero', agregacion)}
                </td>
              );
            }),
          )}
        </tr>,
      );
      if (tieneHijos && abierto) renderNivel(hijo);
    }
  };
  renderNivel(raiz);

  // NOTA: estos son helpers que devuelven JSX (no componentes internos). Si
  // fueran componentes definidos en el render, cada cambio de estado los
  // recrearía y REMONTARÍA los chips → rompería el drag a mitad de camino.
  const renderChip = (campo: Campo, origen: Zona) => (
    <span
      key={campo.id}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', campo.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      className={`inline-flex cursor-grab items-center gap-1 rounded-full border px-2.5 py-1 text-xs ${
        campo.tipo === 'metrica'
          ? 'border-orange-200 bg-orange-50 text-orange-700'
          : 'border-emerald-200 bg-emerald-50 text-emerald-800'
      }`}
    >
      {campo.tipo === 'metrica' ? <span className="font-mono">#</span> : null}
      {campo.etiqueta}
      {origen !== 'disponibles' && (
        <button
          type="button"
          draggable={false}
          onClick={(e) => {
            e.stopPropagation();
            mover(campo.id, 'disponibles');
          }}
          className="rounded-full hover:bg-black/10"
          aria-label={`Quitar ${campo.etiqueta}`}
        >
          <X className="h-3 w-3" weight="bold" />
        </button>
      )}
    </span>
  );

  const onDropZona = (zona: Zona) => (e: DragEvent) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain');
    if (id) mover(id, zona);
  };
  const onDragOverZona = (e: DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const renderZona = (
    zona: Exclude<Zona, 'disponibles'>,
    titulo: string,
    ids: string[],
    acento: string,
  ) => (
    <div
      onDragOver={onDragOverZona}
      onDrop={onDropZona(zona)}
      className={`min-h-[52px] flex-1 rounded-lg border border-dashed p-2 ${acento}`}
    >
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {titulo}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {ids.length === 0 ? (
          <span className="text-[11px] text-zinc-400">Arrastra campos aquí…</span>
        ) : (
          ids.map((id) => {
            const campo = porId.get(id);
            return campo ? renderChip(campo, zona) : null;
          })
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Panel de configuración */}
      <div className="rounded-xl border border-zinc-200 bg-white p-3 shadow-sm">
        {favoritasKey && (
          <div className="mb-3 flex flex-wrap items-center gap-2 border-zinc-100 border-b pb-3 text-xs">
            <span className="flex items-center gap-1 font-semibold text-muted-foreground">
              <Star className="h-3.5 w-3.5" weight="fill" /> Vistas:
            </span>
            {favs.favoritas.length === 0 && (
              <span className="text-[11px] text-zinc-400">Guardá tu combinación favorita →</span>
            )}
            {favs.favoritas.map((f) => (
              <span
                key={f.id}
                className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5"
              >
                <button
                  type="button"
                  onClick={() => aplicarConfig(f.config)}
                  className="hover:underline"
                  title="Aplicar esta vista"
                >
                  {f.nombre}
                </button>
                <button
                  type="button"
                  onClick={() => favs.alternarDefault(f.id)}
                  title={
                    f.esDefault
                      ? 'Vista por defecto al abrir (clic para quitar)'
                      : 'Marcar como vista por defecto al abrir'
                  }
                  className={f.esDefault ? 'text-amber-500' : 'text-zinc-300 hover:text-amber-500'}
                >
                  <Star className="h-3.5 w-3.5" weight={f.esDefault ? 'fill' : 'regular'} />
                </button>
                <button
                  type="button"
                  onClick={() => favs.borrar(f.id)}
                  className="text-zinc-400 hover:text-red-600"
                  title="Borrar vista"
                >
                  <X className="h-3 w-3" weight="bold" />
                </button>
              </span>
            ))}
            <button
              type="button"
              onClick={guardarFav}
              className="rounded-md border border-emerald-700 bg-white px-2 py-0.5 font-medium text-emerald-700 hover:bg-emerald-50"
            >
              + Guardar vista actual
            </button>
            <button type="button" onClick={restablecer} className="text-blue-600 hover:underline">
              Restablecer
            </button>
          </div>
        )}
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Campos disponibles · arrastra a las zonas
        </div>
        <div
          onDragOver={onDragOverZona}
          onDrop={onDropZona('disponibles')}
          className="mb-3 flex flex-wrap gap-1.5"
        >
          {disponibles.map((c) => renderChip(c, 'disponibles'))}
          {disponibles.length === 0 && <span className="text-[11px] text-zinc-400">—</span>}
        </div>
        <div className="flex flex-col gap-2 md:flex-row">
          {renderZona('filas', '↕ Filas', filas, 'border-emerald-300 bg-emerald-50/40')}
          {renderZona('columnas', '→ Columnas', columnas, 'border-blue-300 bg-blue-50/40')}
          {renderZona('metricas', '# Métricas', metricas, 'border-orange-300 bg-orange-50/40')}
        </div>

        {/* Zona de FILTROS: arrastrar una dimensión y elegir qué valores mostrar. */}
        <div
          onDragOver={onDragOverZona}
          onDrop={onDropZona('filtros')}
          className="mt-2 min-h-[52px] rounded-lg border border-violet-300 border-dashed bg-violet-50/40 p-2"
        >
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            ⛃ Filtros
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {filtrosDim.length === 0 ? (
              <span className="text-[11px] text-zinc-400">
                Arrastra una dimensión aquí para filtrar (como en Excel)…
              </span>
            ) : (
              filtrosDim.map((id) => {
                const campo = porId.get(id);
                if (!campo) return null;
                return (
                  <FiltroChip
                    key={id}
                    campo={campo}
                    valores={valoresUnicos.get(id) ?? []}
                    seleccionados={valoresFiltro[id]}
                    onChange={(set) => setValoresFiltro((prev) => ({ ...prev, [id]: set }))}
                    onQuitar={() => quitarFiltro(id)}
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/plain', id);
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                  />
                );
              })
            )}
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Agregación:</span>
          {AGREGACIONES.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => setAgregacion(a)}
              className={`rounded-md px-2.5 py-1 ${
                agregacion === a
                  ? 'bg-emerald-700 text-white'
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
            >
              {a}
            </button>
          ))}
          <span className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => setExpandidos(new Set(ramaKeys))}
              className="text-blue-600 hover:underline"
            >
              Expandir todo
            </button>
            <span className="text-zinc-300">|</span>
            <button
              type="button"
              onClick={() => setExpandidos(new Set())}
              className="text-blue-600 hover:underline"
            >
              Colapsar
            </button>
            <span className="text-zinc-300">|</span>
            <button
              type="button"
              onClick={exportar}
              disabled={!hayMetricas || filas.length === 0}
              className="inline-flex items-center gap-1 rounded-md border border-emerald-700 bg-white px-2 py-1 font-medium text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:border-zinc-300 disabled:text-zinc-400 disabled:hover:bg-white"
              title="Exportar el árbol completo (todos los niveles) a Excel"
            >
              <DownloadSimple className="h-3.5 w-3.5" weight="bold" />
              Exportar a Excel
            </button>
          </span>
        </div>
      </div>

      {/* Tabla */}
      {!hayMetricas || filas.length === 0 ? (
        <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-6 text-center text-sm text-muted-foreground">
          Agregá al menos una dimensión a <strong>Filas</strong> y una métrica a{' '}
          <strong>Métricas</strong> para ver la tabla.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-200">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-emerald-900 text-white">
                <th
                  rowSpan={2}
                  className="sticky left-0 z-20 select-none border-b border-emerald-800 bg-emerald-900 px-2 py-2 text-left text-xs"
                  style={{ ...estiloCol, position: 'sticky' }}
                >
                  {filas.map((f) => porId.get(f)?.etiqueta).join(' › ')}
                  <div
                    onMouseDown={(e) => {
                      e.preventDefault();
                      iniciarResize(e.clientX);
                    }}
                    title="Arrastrar para ajustar el ancho"
                    className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-white/40"
                  />
                </th>
                {gruposCol.map((ck) => (
                  <th
                    key={ck}
                    colSpan={metricas.length}
                    className={`whitespace-nowrap border-b border-l border-emerald-800 px-2 py-2 text-center text-xs ${
                      ck === TOTAL ? 'bg-emerald-950' : ''
                    }`}
                  >
                    {etiquetaGrupo(ck)}
                  </th>
                ))}
              </tr>
              <tr className="bg-emerald-800 text-emerald-50">
                {gruposCol.map((ck) =>
                  metricas.map((m) => {
                    const activo = orden?.ck === ck && orden?.m === m;
                    const flecha = activo ? (orden?.dir === 'desc' ? ' ▼' : ' ▲') : '';
                    return (
                      <th
                        key={`${ck}${SEP}${m}`}
                        className="whitespace-nowrap border-l border-emerald-700 px-2 py-1 text-right text-[11px] font-normal"
                      >
                        <button
                          type="button"
                          // Ciclo: desc -> asc -> sin orden. Clic ordena las filas
                          // (a todo nivel) por esta columna+métrica.
                          onClick={() =>
                            setOrden((prev) => {
                              if (!prev || prev.ck !== ck || prev.m !== m)
                                return { ck, m, dir: 'desc' };
                              if (prev.dir === 'desc') return { ck, m, dir: 'asc' };
                              return null;
                            })
                          }
                          className="inline-flex items-center gap-0.5 hover:underline"
                          title="Ordenar por esta columna"
                        >
                          {porId.get(m)?.etiqueta}
                          <span className="text-emerald-300">{flecha}</span>
                        </button>
                      </th>
                    );
                  }),
                )}
              </tr>
            </thead>
            <tbody>{filasRender}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Chip de filtro (como tabla dinámica de Excel): muestra la dimensión y, al
 * hacer clic, un popup con checkboxes de sus valores. Si no hay selección o
 * están todos marcados, no filtra (muestra "Todos").
 */
function FiltroChip({
  campo,
  valores,
  seleccionados,
  onChange,
  onQuitar,
  onDragStart,
}: {
  campo: Campo;
  valores: string[];
  seleccionados: Set<string> | undefined;
  onChange: (set: Set<string>) => void;
  onQuitar: () => void;
  onDragStart: (e: DragEvent) => void;
}): ReactElement {
  const [abierto, setAbierto] = useState(false);
  const [busca, setBusca] = useState('');
  // Borrador LOCAL de la selección mientras el popup está abierto. Evita
  // recalcular el pivote en cada checkbox (era la causa de la lentitud):
  // solo se aplica al padre (onChange) al cerrar / Aplicar.
  const [borrador, setBorrador] = useState<Set<string>>(() => new Set(seleccionados ?? valores));
  const ref = useRef<HTMLDivElement>(null);

  // Aplica el borrador al padre (dispara un único re-pivote).
  const aplicar = (set: Set<string>) => {
    setAbierto(false);
    onChange(set);
  };

  // Al abrir, sincronizar el borrador con la selección actual.
  useEffect(() => {
    if (abierto) setBorrador(new Set(seleccionados ?? valores));
  }, [abierto, seleccionados, valores]);

  useEffect(() => {
    if (!abierto) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        // Click afuera = aplicar lo que haya en el borrador.
        setAbierto(false);
        onChange(borrador);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [abierto, borrador, onChange]);

  // "Activo" = hay selección parcial (algunos valores, no todos).
  const activo = seleccionados && seleccionados.size > 0 && seleccionados.size < valores.length;
  const resumen = activo ? `${seleccionados.size}/${valores.length}` : 'Todos';
  const filtrados = busca
    ? valores.filter((v) => v.toLowerCase().includes(busca.toLowerCase()))
    : valores;

  // Toggle SOLO en el borrador local (no recalcula la tabla todavía).
  const toggleValor = (v: string) => {
    setBorrador((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
  };

  return (
    <div className="relative" ref={ref}>
      <span
        draggable
        onDragStart={onDragStart}
        className={`inline-flex cursor-grab items-center gap-1 rounded-full border px-2.5 py-1 text-xs ${
          activo
            ? 'border-violet-400 bg-violet-100 text-violet-800'
            : 'border-violet-200 bg-violet-50 text-violet-700'
        }`}
      >
        <button
          type="button"
          onClick={() => setAbierto((v) => !v)}
          className="flex items-center gap-1"
        >
          <span className="font-medium">{campo.etiqueta}</span>
          <span className="text-[10px] opacity-70">({resumen})</span>
          <CaretDown className="h-3 w-3" weight="bold" />
        </button>
        <button type="button" onClick={onQuitar} className="hover:text-red-600" title="Quitar">
          <X className="h-3 w-3" weight="bold" />
        </button>
      </span>
      {abierto && (
        <div className="absolute left-0 z-50 mt-1 w-56 overflow-hidden rounded-md border border-zinc-200 bg-white shadow-lg">
          <div className="border-zinc-100 border-b p-2">
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar…"
              className="w-full rounded border border-zinc-300 px-2 py-1 text-xs"
            />
            <div className="mt-1.5 flex gap-2 text-[11px]">
              <button
                type="button"
                onClick={() => setBorrador(new Set(valores))}
                className="text-blue-600 hover:underline"
              >
                Todos
              </button>
              <button
                type="button"
                onClick={() => setBorrador(new Set())}
                className="text-blue-600 hover:underline"
              >
                Ninguno
              </button>
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto p-1">
            {filtrados.map((v) => {
              const marcado = borrador.has(v);
              return (
                <label
                  key={v}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-zinc-50"
                >
                  <input type="checkbox" checked={marcado} onChange={() => toggleValor(v)} />
                  <span className="truncate">{v}</span>
                </label>
              );
            })}
            {filtrados.length === 0 && (
              <p className="px-2 py-1 text-[11px] text-muted-foreground">Sin resultados</p>
            )}
          </div>
          <div className="flex justify-end gap-2 border-zinc-100 border-t p-2">
            <button
              type="button"
              onClick={() => setAbierto(false)}
              className="rounded px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-100"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => aplicar(borrador)}
              className="rounded bg-emerald-700 px-3 py-1 text-xs text-white hover:bg-emerald-800"
            >
              Aplicar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
